import { useMemo } from 'react';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useBattleCardsStore } from 'store/battlecards.store';

import { useMutationDownloadCsvFile } from 'api/mutations/downloadCsvFile.mutation';
import { useMutationExportXerOrMpp } from 'api/mutations/exportXerOrMpp.mutation';
import { useMutationRowsBoardToCsv } from 'api/mutations/extractRowsBoardToCsv.mutation';
import { useMutationRowsBoardToXls } from 'api/mutations/extractRowsBoardToXls.mutation';
import { useQueryListCardsData } from 'api/queries/getListFilteredCards.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { createSpecialFiltersPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';

interface Props {
    widgetId?: string;
    latestVersionId: number;
    localFilters: any;
    globalFilterData: any;
    projectId: number;
    contractId: number;
    table: UseBaseTable['table'];
    endpoints: Record<string, string>;
}

export type UseRowsBoardProvider = ReturnType<typeof useRowsBoardProvider>;

export const useRowsBoardProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    table,
    endpoints,
    globalFilterData,
    projectId,
    contractId,
}: Props) => {
    const displayStyle = useBattleCardsStore((state) => state.displayStyle);
    const isInPinboard = Boolean(widgetId);

    const source = isInPinboard ? { ...localFilters } : { ...globalFilterData, displayStyle };
    let filters = useMemo(() => {
        if (!projectId || !contractId || !latestVersionId) return { filtersArray: [], flags: {} };

        const baseFilters = getFiltersData({
            [FilterNames.PROJECT_CARD_BOARD]: [projectId],
            [FilterNames.CONTRACT_CARD_BOARD]: [contractId],
            [FilterNames.VERSION_CARD_BOARD]: [latestVersionId],
            [FilterNames.ACTIVITY_TYPE_BOARD]: source?.cardActivity,
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.ASSIGNEE_CARD_BOARD]: source?.assignee,
            [FilterNames.IMPORTANT_CARD_BOARD]: source?.important,
            [FilterNames.URGENT_TYPE_CARD_BOARD]: source?.urgent,
            [FilterNames.FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD]: source?.completionPlanSegment,
            [FilterNames.DOCLINK_CARD_BOARD]: source?.hasAttachments,
            [FilterNames.LAG_CARD_BOARD]: source?.lagLead,
            [FilterNames.TRACKED_CARD_BOARD]: source?.trackedActivity,
            [FilterNames.FRAGNET_FORWARD_SEARCH]: source?.activityForwardFragnet,
            [FilterNames.TYPE_CARD_BOARD]: source?.cardType,
            [FilterNames.CLUSTER_CARD_BOARD]: source?.cluster,
            [FilterNames.DURATION_CARD_BOARD]: source?.duration,
            [FilterNames.TOTAL_FLOAT_CARD_BOARD]: source?.float,
            [FilterNames.ACTIVITY_NAME_CARD_BOARD]: source?.relatedActivity,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: source?.resourceName,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: source?.resourceType,
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: source?.submittedCompletion,
            [FilterNames.UPDATED_USER_CARD_BOARD]: source?.updatedBy,
            [FilterNames.WBS_LEVEL_CARD_BOARD]: source?.wbsLevel,
            [FilterNames.STATUS_CARD_BOARD]: source.status,
        });

        const specialFiltersPayload = createSpecialFiltersPayload(source);
        const specialFilters = specialFiltersPayload?.filters ?? [];
        const flags = specialFiltersPayload?.flags ?? {};

        return { filtersArray: [...baseFilters, ...specialFilters], flags };
    }, [projectId, contractId, latestVersionId, source]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        cardActivity: source?.cardActivity,
        assignee: source?.assignee,
        important: source?.important,
        urgent: source?.urgent,
        completionPlanSegment: source?.completionPlanSegment,
        hasAttachments: source?.hasAttachments,
        lagLead: source?.lagLead,
        trackedActivity: source?.trackedActivity,
        activityForwardFragnet: source?.activityForwardFragnet,
        cardType: source?.cardType,
        cluster: source?.cluster,
        duration: source?.duration,
        float: source?.float,
        relatedActivity: source?.relatedActivity,
        resourceName: source?.resourceName,
        submittedCompletion: source?.submittedCompletion,
        updatedBy: source?.updatedBy,
        wbsLevel: source?.wbsLevel,
        status: source?.status,
        resourceType: source?.resourceType,
        milestoneFragnet: source?.milestoneFragnet,
        wbs: source?.wbs,
        finishDate: source?.finishDate,
        startDate: source?.startDate,
        baselineFinishDate: source?.baselineFinishDate,
        baselineStartDate: source?.baselineStartDate,
        hasComments: source?.hasComments,
        riskIndicators: source?.riskIndicators,
        searchByName: source?.searchByName,
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
    };

    const getListCardsDataPayload = {
        cardCachedFilter: {
            filter: {
                filters: filters.filtersArray,
            },
            flags: filters.flags,
        },
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        sortingRequestList: table.sorting,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    return {
        ui: {
            state: {
                projectId,
                contractId,
                latestVersionId,
                cardActivity: source?.cardActivity,
                assignee: source?.assignee,
                important: source?.important,
                urgent: source?.urgent,
                completionPlanSegment: source?.completionPlanSegment,
                hasAttachments: source?.hasAttachments,
                lagLead: source?.lagLead,
                trackedActivity: source?.trackedActivity,
                activityForwardFragnet: source?.activityForwardFragnet,
                cardType: source?.cardType,
                clusterType: source?.clusterType,
                duration: source?.duration,
                float: source?.float,
                relatedActivity: source?.relatedActivity,
                resourceName: source?.resourceName,
                submittedCompletion: source?.submittedCompletion,
                updatedBy: source?.updatedBy,
                wbsLevel: source?.wbsLevel,
                status: source?.status,
                resourceType: source?.resourceType,
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
                finishDate: source?.finishDate,
                startDate: source?.startDate,
                baselineFinishDate: source?.baselineFinishDate,
                baselineStartDate: source?.baselineStartDate,
                hasComments: source?.hasComments,
                riskIndicators: source?.riskIndicators,
                searchByName: source?.searchByName,
                displayStyle: source?.displayStyle,
            },
            pills: pills,
            isInPinboard,
        },
        api: {
            listCardsData: {
                instance: useQueryListCardsData(getListCardsDataPayload, endpoints.getListFilteredCards),
                payload: getListCardsDataPayload,
                endpoint: endpoints.getListFilteredCards,
            },
            downloadCsvFile: {
                instance: useMutationDownloadCsvFile(),
            },
            rowsBoardToCsv: {
                instance: useMutationRowsBoardToCsv(),
                payload: {
                    filter: {
                        filter: {
                            filters: filters.filtersArray,
                        },
                        flags: filters.flags,
                        paging: { ...table.pagination, page: table.pagination.page - 1 },
                        //:TODO CHECK IF withDetails NECESSARY
                        withDetails: true,
                    },
                },
            },
            rowsBoardToXls: {
                instance: useMutationRowsBoardToXls(),
                payload: {
                    filter: {
                        filter: {
                            filters: filters.filtersArray,
                        },
                        flags: filters.flags,
                        paging: { ...table.pagination, page: table.pagination.page - 1 },
                        //:TODO CHECK IF withDetails NECESSARY
                        withDetails: true,
                    },
                },
            },
            exportXerOrMpp: {
                instance: useMutationExportXerOrMpp(),
            },
        },
    };
};
